'use client';

import { useMemo, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { toast } from 'sonner';

import { useApiV1Ctx } from '@/components/providers/v1-api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';

export function SetupOrgDialog() {
  const [open, setOpen] = useState(false);
  const { api } = useApiV1Ctx();
  const { mutate, isPending } = api.organizations.setupOrganizationsSetupPost.useMutation(
    {},
    {
      onSuccess: () => {
        toast.success('Employee created');
        api.employees.getAllEmployeesGet.invalidateQueries();
        setOpen(false);
      },
    }
  );

  const [name, setName] = useState('');
  const [employee, setEmployee] = useState({
    first_name: '',
    last_name: '',
    email: '',
  });

  const isDisabled = useMemo(() => {
    if (!name) return true;
    if (!employee.email) return true;
    if (!employee.first_name && !employee.last_name) return true;
    if (isPending) return true;
    return false;
  }, [employee, isPending, name]);

  const onSubmit = () => {
    mutate({
      name: name,
      status: 'ACTIVE',
      employee_email: employee.email,
      employee_first_name: employee.first_name,
      employee_last_name: employee.last_name,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className='h-4 w-4' />
          Setup New Organization
        </Button>
      </DialogTrigger>
      <DialogContent showClose>
        <DialogHeader>
          <DialogTitle>Setup New Organization</DialogTitle>
          <DialogDescription>Creates an organization, office and employee.</DialogDescription>
        </DialogHeader>
        <div className='grid gap-4 py-4'>
          <div className='grid grid-cols-4 items-center gap-2'>
            <Label htmlFor='name'>Name</Label>
            <Input id='name' value={name} onChange={(e) => setName(e.target.value)} className='col-span-3' />
          </div>
          <Separator />
          <div className='text-sm font-medium'>Employee Information</div>
          <div className='grid grid-cols-4 items-center gap-2'>
            <Label htmlFor='employee_first_name'>First Name</Label>
            <Input
              id='employee_first_name'
              value={employee.first_name}
              onChange={(e) => setEmployee({ ...employee, first_name: e.target.value })}
              className='col-span-3'
            />
          </div>
          <div className='grid grid-cols-4 items-center gap-2'>
            <Label htmlFor='employee_last_name'>Last Name</Label>
            <Input
              id='employee_last_name'
              value={employee.last_name}
              onChange={(e) => setEmployee({ ...employee, last_name: e.target.value })}
              className='col-span-3'
            />
          </div>
          <div className='grid grid-cols-4 items-center gap-2'>
            <Label htmlFor='email'>Email</Label>
            <Input
              id='email'
              type='email'
              value={employee.email}
              onChange={(e) => setEmployee({ ...employee, email: e.target.value })}
              className='col-span-3'
              required
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Cancel</Button>
          </DialogClose>
          <Button type='submit' onClick={onSubmit} disabled={isDisabled}>
            {isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
